import type { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { readonly, ref, watch, type Ref } from 'vue';
import { useAuthUser } from '@/helpers/globals';
import { ECH_STATUS } from '@/models/Status';
import { instance as api } from '@/helpers/api';

async function calcIsReadonly(geschaeft: Planungsgeschaeft | null): Promise<boolean> {
    if (useAuthUser()?.isRoleAdmin) {
        // admin is always allowed to edit the geschaeft
        return false;
    }

    // can be written if the Geschaeft is:
    // - newly created
    // - additional documents are requested
    if (
        geschaeft?.aktueller_status_ebau_value !== ECH_STATUS.STATUS_NEW &&
        geschaeft?.aktueller_status_ebau_value !== ECH_STATUS.DEMAND_DOCUMENTS
    ) {
        return true;
    }

    const remoteReadonly = await loadAccessControlReadonly(geschaeft?.id);
    if (remoteReadonly) {
        return true;
    }

    return false;
}

/**
 * Loads the read-only status of the Planungsgeschaeft from the backend, and returns
 * true/false as a promise.
 *
 * @param planungsgeschaeftId
 */
export async function loadAccessControlReadonly(planungsgeschaeftId: number | null): Promise<boolean> {
    if (!planungsgeschaeftId) {
        return true; // is readonly
    }

    try {
        const res = await api().post('/AccessControl/isReadonlyPpa', {
            planning_permission_application_id: planungsgeschaeftId
        });
        return res?.data?.readonly === true;
    } catch (e) {
        console.error(e);
        return true;
    }
}

type UsePlanungsgeschaeftAccessControlInfo = {
    readonly: Ref<boolean>;
    reevaluate(): Promise<boolean>;
};

/**
 * Returns reactive properties to indicate a Geschaeft's access state:
 * - readonly: is the geschaeft read-only state for the actual Geschaeft's state and user
 * - reevaluate(): function to reload the access state and updating the ref vars manually
 *
 * Note that the Geschaeft record itself is NOT deeply watched: It does NOT re-evaluate the Geschaeft when
 * a property of the Geschaeft record changes. This can be achieved by calling reevaluate() manually, e.g.
 * after a Save happened.
 *
 * @param Ref<Planungsgeschaeft> geschaeft A ref variable of a Planungsgeschaeft record
 */
export function usePlanungsgeschaeftAccessControl(geschaeft: Ref<Planungsgeschaeft | null>): UsePlanungsgeschaeftAccessControlInfo {
    const isReadonly = ref(true);
    calcIsReadonly(geschaeft.value).then((value) => (isReadonly.value = value));

    // shallow watch the user: no deep prop watch here:
    watch(
        () => useAuthUser(),
        async () => {
            isReadonly.value = await calcIsReadonly(geschaeft.value);
        },
    );

    // shallow watch the geschaeft: no deep prop watch here:
    watch(
        geschaeft,
        async () => {
            isReadonly.value = await calcIsReadonly(geschaeft.value);
        },
    );

    return {
        readonly: readonly(isReadonly),
        async reevaluate() {
            return await calcIsReadonly(geschaeft.value);
        }
    } as UsePlanungsgeschaeftAccessControlInfo;
}
